import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

interface SenderRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPlan: "FREE" | "BASIC" | "PRO" | "ENTERPRISE";
  number: string;
  name: string;
  status: "pending" | "approved" | "rejected";
  submittedAt: string;
  reviewedAt?: string;
  reviewer?: string;
  rejectReason?: string;
  documents: string[];
  note?: string;
}

const PLAN_COLORS: Record<string, string> = {
  FREE: "text-gray-400 bg-gray-500/10 border-gray-500/20",
  BASIC: "text-sky-400 bg-sky-500/10 border-sky-500/20",
  PRO: "text-violet-400 bg-violet-500/10 border-violet-500/20",
  ENTERPRISE: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

const STATUS_CONFIG = {
  pending: { label: "심사 대기", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", icon: "ri-time-line" },
  approved: { label: "승인 완료", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", icon: "ri-checkbox-circle-line" },
  rejected: { label: "반려됨", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", icon: "ri-close-circle-line" },
};

function getStatusConfig(status?: string | null) {
  return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? {
    label: status || "알 수 없음",
    color: "text-gray-400",
    bg: "bg-gray-500/10 border-gray-500/20",
    icon: "ri-question-line",
  };
}

function getPlanColor(plan?: string | null) {
  return PLAN_COLORS[plan || ""] || "text-gray-400 bg-gray-500/10 border-gray-500/20";
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).replace(/\. /g, ".").replace(/\.$/, "");
}

interface DetailModalProps {
  request: SenderRequest;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
}

function DetailModal({ request, onClose, onApprove, onReject }: DetailModalProps) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [confirmApprove, setConfirmApprove] = useState(false);

  const statusCfg = getStatusConfig(request.status);
  const planColor = getPlanColor(request.userPlan);

  const handleApprove = () => {
    if (!confirmApprove) { setConfirmApprove(true); return; }
    onApprove(request.id);
    onClose();
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) { setRejectError("반려 사유를 입력해주세요."); return; }
    onReject(request.id, rejectReason.trim());
    onClose();
  };

  const PRESET_REASONS = [
    "제출 서류가 유효기간이 만료되었습니다.",
    "사업자 정보와 일치하지 않는 번호입니다.",
    "필수 서류가 누락되었습니다.",
    "번호 형식이 올바르지 않습니다.",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0d0d1a] border-b border-white/[0.07] p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-500/10">
              <i className="ri-phone-line text-amber-400 text-lg"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">발신번호 심사 상세</h3>
              <p className="text-gray-500 text-xs">신청 ID: {request.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold ${statusCfg.bg} ${statusCfg.color}`}>
              <i className={statusCfg.icon}></i>
              {statusCfg.label}
            </span>
            {request.reviewedAt && (
              <span className="text-gray-600 text-xs">심사 완료: {request.reviewedAt}</span>
            )}
          </div>

          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 space-y-3">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest">신청자 정보</p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/40 to-pink-500/40 text-white text-sm font-bold shrink-0">
                {request.userName.charAt(0)}
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{request.userName}</p>
                <p className="text-gray-500 text-xs">{request.userEmail}</p>
              </div>
              <span className={`ml-auto px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${planColor}`}>
                {request.userPlan}
              </span>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 space-y-3">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest">발신번호 정보</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600 text-xs mb-1">전화번호</p>
                <p className="text-white text-sm font-bold tracking-wide">{request.number}</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs mb-1">발신번호 이름</p>
                <p className="text-white text-sm font-semibold">{request.name}</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs mb-1">신청일시</p>
                <p className="text-gray-300 text-sm">{request.submittedAt}</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs mb-1">신청자 ID</p>
                <p className="text-gray-400 text-xs font-mono">{request.userId}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 space-y-3">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest">첨부 서류 ({request.documents.length}건)</p>
            <div className="space-y-2">
              {request.documents.length === 0 ? (
                <p className="text-gray-600 text-xs">첨부된 서류가 없습니다.</p>
              ) : (
                request.documents.map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`${typeof doc === "string" && doc.endsWith(".pdf") ? "ri-file-pdf-line text-red-400" : "ri-image-line text-sky-400"} text-base`}></i>
                    </div>
                    <span className="text-gray-300 text-xs flex-1 truncate">{doc}</span>
                    <button className="text-gray-500 hover:text-white cursor-pointer transition-colors text-xs whitespace-nowrap">
                      <i className="ri-download-line"></i>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {request.note && (
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-2">신청자 메모</p>
              <p className="text-gray-300 text-sm leading-relaxed">{request.note}</p>
            </div>
          )}

          {request.status === "rejected" && request.rejectReason && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-red-400 text-xs font-semibold mb-2"><i className="ri-close-circle-line mr-1"></i>반려 사유</p>
              <p className="text-red-300/80 text-sm leading-relaxed">{request.rejectReason}</p>
            </div>
          )}

          {confirmApprove && !rejectMode && request.status === "pending" && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-checkbox-circle-line text-emerald-400"></i>
                <p className="text-emerald-300 text-sm font-semibold">승인하시겠습니까?</p>
              </div>
              <p className="text-emerald-400/70 text-xs leading-relaxed">
                승인 시 해당 번호가 즉시 발신번호로 활성화되며, 신청자에게 알림이 발송됩니다.
              </p>
            </div>
          )}

          {rejectMode && request.status === "pending" && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-3">
              <p className="text-red-400 text-sm font-semibold"><i className="ri-close-circle-line mr-1"></i>반려 사유 입력</p>
              <div className="space-y-2">
                <p className="text-gray-500 text-xs">빠른 선택</p>
                <div className="flex flex-wrap gap-2">
                  {PRESET_REASONS.map((reason) => (
                    <button
                      key={reason}
                      onClick={() => { setRejectReason(reason); setRejectError(""); }}
                      className={`px-2.5 py-1 rounded-lg border text-xs cursor-pointer transition-all whitespace-nowrap ${
                        rejectReason === reason
                          ? "bg-red-500/20 border-red-500/40 text-red-300"
                          : "bg-white/[0.03] border-white/[0.08] text-gray-400 hover:border-white/[0.15]"
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                value={rejectReason}
                onChange={(e) => { setRejectReason(e.target.value); setRejectError(""); }}
                placeholder="반려 사유를 직접 입력하세요..."
                rows={3}
                maxLength={300}
                className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-red-500/50 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none transition-all resize-none"
              />
              {rejectError && <p className="text-red-400 text-xs">{rejectError}</p>}
              <p className="text-gray-600 text-xs text-right">{rejectReason.length}/300</p>
            </div>
          )}

          {request.status === "pending" && (
            <div className="flex gap-3">
              {!rejectMode ? (
                <>
                  <button
                    onClick={() => { setRejectMode(true); setConfirmApprove(false); }}
                    className="flex-1 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                  >
                    <i className="ri-close-line mr-1.5"></i>
                    반려
                  </button>
                  <button
                    onClick={handleApprove}
                    className={`flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${
                      confirmApprove
                        ? "bg-emerald-500 text-white"
                        : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
                    }`}
                  >
                    <i className="ri-checkbox-circle-line mr-1.5"></i>
                    {confirmApprove ? "최종 승인 확정" : "승인"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setRejectMode(false); setRejectReason(""); setRejectError(""); }}
                    className="flex-1 py-3 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleRejectSubmit}
                    className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 text-sm font-bold cursor-pointer whitespace-nowrap transition-all"
                  >
                    <i className="ri-close-circle-line mr-1.5"></i>
                    반려 확정
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SenderReviewTab() {
  const { session } = useAuth();
  const [requests, setRequests] = useState<SenderRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<SenderRequest | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 8;

  const accessToken = session?.access_token;

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRequests = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-sender-numbers?status=${statusFilter}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const mapped: SenderRequest[] = (json.numbers ?? []).map((n: any) => ({
        id: n.id,
        userId: n.user_id,
        userName: n.profiles?.full_name || "이름 없음",
        userEmail: n.profiles?.email || "-",
        userPlan: (n.profiles?.plan || "FREE").toUpperCase() as any,
        number: n.number,
        name: n.name,
        status: n.status as "pending" | "approved" | "rejected",
        submittedAt: formatDate(n.created_at),
        reviewedAt: n.reviewed_at ? formatDate(n.reviewed_at) : undefined,
        reviewer: n.reviewer || undefined,
        rejectReason: n.reject_reason || undefined,
        documents: n.documents || [],
        note: n.note || undefined,
      }));
      setRequests(mapped);
    } catch (err: any) {
      console.error("[SenderReview] fetch error:", err);
      setError(err.message || "발신번호 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (id: string, status: "approved" | "rejected", rejectReason?: string) => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-sender-status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, status, reject_reason: rejectReason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      await fetchRequests();
      if (status === "approved") {
        showToast("발신번호가 승인되었습니다. 신청자에게 알림이 발송됩니다.", "success");
      } else {
        showToast("발신번호 신청이 반려되었습니다.", "error");
      }
    } catch (err: any) {
      console.error("[SenderReview] update error:", err);
      showToast(err.message || "처리 중 오류가 발생했습니다.", "error");
    }
  };

  const handleApprove = (id: string) => updateStatus(id, "approved");
  const handleReject = (id: string, reason: string) => updateStatus(id, "rejected", reason);

  const filtered = requests.filter((r) => {
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || r.number.includes(q) || r.userName.toLowerCase().includes(q) || r.userEmail.toLowerCase().includes(q) || r.name.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const total = filtered.length;
  const totalPages = Math.ceil(total / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const counts = {
    all: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.status === "approved").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
  };

  const tabs: { key: typeof statusFilter; label: string; color: string }[] = [
    { key: "all", label: "전체", color: "text-gray-300" },
    { key: "pending", label: "심사 대기", color: "text-amber-400" },
    { key: "approved", label: "승인 완료", color: "text-emerald-400" },
    { key: "rejected", label: "반려됨", color: "text-red-400" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-white font-bold text-lg">발신번호 인증 심사</h2>
          <p className="text-gray-500 text-sm mt-0.5">사용자가 신청한 발신번호의 인증 심사를 처리합니다</p>
        </div>
        {counts.pending > 0 && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2 shrink-0">
            <i className="ri-time-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-semibold whitespace-nowrap">대기 {counts.pending}건</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "전체 신청", value: counts.all, icon: "ri-phone-line", color: "text-gray-300", bg: "bg-white/[0.03]" },
          { label: "심사 대기", value: counts.pending, icon: "ri-time-line", color: "text-amber-400", bg: "bg-amber-500/5" },
          { label: "승인 완료", value: counts.approved, icon: "ri-checkbox-circle-line", color: "text-emerald-400", bg: "bg-emerald-500/5" },
          { label: "반려됨", value: counts.rejected, icon: "ri-close-circle-line", color: "text-red-400", bg: "bg-red-500/5" },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} border border-white/[0.07] rounded-xl p-4 flex items-center gap-3`}>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.04] shrink-0">
              <i className={`${card.icon} text-xl ${card.color}`}></i>
            </div>
            <div>
              <p className="text-white font-bold text-2xl leading-none">{card.value}</p>
              <p className="text-gray-500 text-xs mt-1">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center bg-white/[0.03] border border-white/[0.07] rounded-xl p-1 gap-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setStatusFilter(tab.key); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                statusFilter === tab.key
                  ? "bg-white/[0.1] text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <span className={statusFilter === tab.key ? tab.color : ""}>{tab.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                statusFilter === tab.key ? "bg-white/[0.15] text-white" : "bg-white/[0.05] text-gray-500"
              }`}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-72 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-search-line text-gray-500 text-sm"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="번호, 이름, 이메일 검색"
            className="flex-1 bg-transparent text-white text-xs placeholder-gray-600 focus:outline-none"
          />
          {search && (
            <button onClick={() => setSearch("")} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer">
              <i className="ri-close-line text-xs"></i>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/[0.07] rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.07]">
              {["신청자", "발신번호", "플랜", "신청일시", "상태", "처리"].map((h) => (
                <th key={h} className="px-4 py-3.5 text-left text-gray-500 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    불러오는 중...
                  </div>
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-600 text-sm">
                  <i className="ri-search-line text-2xl mb-2 block"></i>
                  {error ? "오류가 발생하여 데이터를 표시할 수 없습니다" : "검색 결과가 없습니다"}
                </td>
              </tr>
            ) : (
              paged.map((req) => {
                const statusCfg = getStatusConfig(req.status);
                const planColor = getPlanColor(req.userPlan);
                return (
                  <tr
                    key={req.id}
                    onClick={() => setSelectedRequest(req)}
                    className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer transition-colors group"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/30 to-pink-500/30 text-white text-xs font-bold shrink-0">
                          {req.userName.charAt(0)}
                        </div>
                        <div>
                          <p className="text-white text-xs font-semibold whitespace-nowrap">{req.userName}</p>
                          <p className="text-gray-500 text-[11px] whitespace-nowrap">{req.userEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-white text-sm font-bold whitespace-nowrap">{req.number}</p>
                      <p className="text-gray-500 text-xs whitespace-nowrap">{req.name}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${planColor}`}>
                        {req.userPlan}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-gray-300 text-xs whitespace-nowrap">{req.submittedAt}</p>
                      {req.reviewedAt && (
                        <p className="text-gray-600 text-[11px] whitespace-nowrap">심사: {req.reviewedAt}</p>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap w-fit ${statusCfg.bg} ${statusCfg.color}`}>
                        <i className={statusCfg.icon}></i>
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {req.status === "pending" ? (
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleApprove(req.id); }}
                            className="px-2.5 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                          >
                            <i className="ri-checkbox-circle-line mr-1"></i>승인
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedRequest(req); }}
                            className="px-2.5 py-1.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                          >
                            <i className="ri-close-line mr-1"></i>반려
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedRequest(req); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-xs cursor-pointer whitespace-nowrap"
                        >
                          상세보기
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-gray-600 text-xs">{total}건 중 {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}건 표시</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                  p === page ? "bg-rose-500/20 border border-rose-500/30 text-rose-400" : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      )}

      {selectedRequest && (
        <DetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
          toast.type === "success"
            ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
            : "bg-red-500/20 border-red-500/30 text-red-300"
        }`}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
}